import { useRef, useState } from "react";

import { api, uploadBytes, type Vehicle, type VehiclePhotoUpload } from "../api";
import { downscaleForInference } from "../downscale";

/** Mirrors `ALLOWED_PHOTO_TYPES` in the API's `media.py`. Rejecting here saves a pointless round trip. */
const ACCEPTED = ["image/jpeg", "image/png", "image/heic", "image/heif", "image/webp"];
const MAX_BYTES = 25 * 1024 * 1024;

interface Props {
  teamId: string;
  vehicle: Vehicle;
  onClose: () => void;
  /** Fired once the upload is recorded and inference has been kicked off, not when the cutout lands. */
  onStarted: (vehicle: Vehicle) => void;
  onError: (message: string) => void;
}

type Phase = "coach" | "review" | "uploading";

/**
 * Guided vehicle capture. Task 9.3, requirement 7.1.
 *
 * The coaching is the feature. Background removal on a toy truck photographed on a patterned rug in
 * bad light produces a mask with the rug in it, and requirement 7.6 will correctly refuse that mask and
 * fall back — so the family gets a framed snapshot instead of a cutout. Every instruction here is aimed
 * at the mask, and **nose facing left** is the one that has nothing to do with segmentation quality: it
 * is what makes forty cards look like a set rather than forty photographs. A truck facing right in a
 * grid of trucks facing left reads as a mistake even to someone who cannot say why.
 *
 * Deliberately not a live camera preview. `getUserMedia` would allow a framing overlay, but it needs
 * HTTPS plus a permission prompt, gives a worse sensor pipeline than the camera app, and cannot match
 * the native shutter. `capture="environment"` hands the phone's own camera the job and gets HDR,
 * stabilisation and a viewfinder the user already knows for free. The overlay moves to the review step
 * instead, where it can be checked against the actual photograph.
 */
export function PhotoCapture({ teamId, vehicle, onClose, onStarted, onError }: Props) {
  const [phase, setPhase] = useState<Phase>("coach");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  const choose = (chosen: File | null) => {
    if (!chosen) return;
    if (chosen.size > MAX_BYTES) {
      onError("That photo is too large. Try a smaller one.");
      return;
    }
    // An empty `type` happens with some Android pickers. The extension is not authoritative either, so
    // the safe assumption is the one the camera almost always produces.
    const type = ACCEPTED.includes(chosen.type) ? chosen.type : "";
    if (chosen.type && !type) {
      onError("That file isn't a photo we can use. JPEG, PNG, HEIC or WebP.");
      return;
    }
    setFile(chosen);
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(chosen);
    });
    setPhase("review");
  };

  const upload = async () => {
    if (!file) return;
    setPhase("uploading");
    setProgress(0);
    try {
      const contentType = ACCEPTED.includes(file.type) ? file.type : "image/jpeg";
      // Three steps, and the bytes never touch our API: it hands back a signature, the browser PUTs to
      // S3 directly, and only then does the API get told to look. Design §6.3.
      const upload: VehiclePhotoUpload = await api.startPhoto(teamId, vehicle.vehicleId, contentType);

      // The original goes up untouched, because requirement 7.2 keeps it forever.
      await uploadBytes(upload, file, setProgress);

      // And a downscaled copy when the original is over the model's ceiling, which a phone photograph
      // always is. Without this Bedrock refuses the photo outright and the vehicle ends up with no
      // cutout for a reason that looks like bad lighting. Returns null when no copy is needed.
      const forInference = await downscaleForInference(file);
      if (forInference) {
        await uploadBytes(upload.inference, forInference).catch(() => {});
      }

      // Hands the updated vehicle back rather than merely saying "go and look". The server has
      // already marked it `processing`, so the row shows "Cutting out…" the instant the sheet closes
      // instead of after a full garage re-read — which is what made photographing several trucks in a
      // row feel like each one took longer than the last.
      onStarted(await api.finishPhoto(teamId, vehicle.vehicleId, upload.key));
      onClose();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : "The upload didn't finish.");
      setPhase("review");
    }
  };

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={`Photograph ${vehicle.name ?? "truck"}`}>
      <div className="sheet-inner">
        <div className="row">
          <p className="section-title grow" style={{ margin: 0 }}>
            {vehicle.name ?? "Truck"}
          </p>
          <button className="btn small" onClick={onClose} disabled={phase === "uploading"}>
            Close
          </button>
        </div>

        {phase === "coach" && (
          <>
            <div className="shot-guide" aria-hidden="true">
              <span className="shot-truck">🚚</span>
              <span className="shot-arrow">←</span>
            </div>
            <p className="muted" style={{ textAlign: "center" }}>
              Four things, and the cutout comes out clean.
            </p>
            <ol className="coach">
              <li>
                <strong>Plain surface.</strong> A worktop, a table, a sheet of paper. Anything patterned
                ends up in the cutout.
              </li>
              <li>
                <strong>From the side.</strong> Crouch to the truck's height rather than shooting down
                at it.
              </li>
              <li>
                <strong>Nose pointing left.</strong> Every truck faces the same way, so the whole garage
                looks like a set.
              </li>
              <li>
                <strong>Fill the frame.</strong> Get close. Wheel to wheel, edge to edge.
              </li>
            </ol>
            <button className="btn primary wide" onClick={() => input.current?.click()}>
              Take the photo
            </button>
          </>
        )}

        {phase === "review" && preview && (
          <>
            {/* The overlay lives here rather than over a viewfinder: this is the first moment the
                framing can be checked against a real photograph instead of an intention. */}
            <div className="shot-review">
              <img src={preview} alt="The photo you just took" />
              <span className="shot-arrow over" aria-hidden="true">
                ←
              </span>
            </div>
            <p className="muted" style={{ textAlign: "center" }}>
              Is the nose pointing left and filling the frame?
            </p>
            <button className="btn primary wide" onClick={() => void upload()}>
              Use this photo
            </button>
            <button className="btn wide" onClick={() => input.current?.click()}>
              Retake
            </button>
          </>
        )}

        {phase === "uploading" && (
          <div className="centre" style={{ padding: "34px 8px" }}>
            <div className="spinner" />
            <p className="muted">
              {progress < 1 ? `Uploading ${Math.round(progress * 100)}%` : "Starting the cutout…"}
            </p>
          </div>
        )}

        <input
          ref={input}
          type="file"
          accept={ACCEPTED.join(",")}
          // Asks for the rear camera directly. Ignored on a desktop browser, which falls back to a file
          // picker — the same flow, one fewer tap saved.
          capture="environment"
          hidden
          onChange={(event) => {
            choose(event.target.files?.[0] ?? null);
            // Cleared so retaking the *same* file still fires a change event.
            event.target.value = "";
          }}
        />
      </div>
    </div>
  );
}
